import { AloftDate, AloftPrice, expected, isUndefined } from '@aloft/toolkit-shared';
import { IsoMonth } from '@aloft/toolkit-shared/lib/syntax/fnma/types';
import { Block, Flex, Tabs } from '@aloft/toolkit-sui';

import { debounce } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Form, FormProvider, FormState } from 'react-hook-form';

import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import {
  setMarketAdjustments,
  setMarketAdjustmentsCommentary,
} from '../../redux/features/orderSlice.ts';
import { setManualAdjustments } from '../../redux/features/propertyDataSlice.ts';
import {
  makeCustomMonthlyPriceInput,
  makeZillowMethodInput,
} from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import {
  selectCompSequence,
  selectEffectiveDate,
  selectMarketAdjustments,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectTransactionEntities } from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectSubjectValue } from '../../redux/features/selectors/selectSubjectValue.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { CompCard } from './CompCard.tsx';
import { DateOfSaleTab } from './DateOfSaleTab.tsx';
import { ListToSaleTab } from './ListToSaleTab.tsx';
import { useMarketAdjustmentsModalForm } from './hooks/useMarketAdjustmentsModalForm.ts';
import { MarketAdjustmentsModalForm } from './types.ts';
import { formManualAdjustmentsToState } from './utils/formManualAdjustmentsToState.ts';
import { formMarketAdjustmentsToState } from './utils/formMarketAdjustmentsToState.ts';
import { inputStringToNumber } from './utils/inputStringToNumber.ts';

type MarketAdjustmentsTab = 'dateOfSale' | 'listToSale';
const marketAdjustmentsTabs: { label: string; value: MarketAdjustmentsTab }[] = [
  { label: 'Date of Sale', value: 'dateOfSale' },
  { label: 'List to Sale', value: 'listToSale' },
];

export const MarketAdjustmentsModule = () => {
  const [activeMarketAdjustmentsTab, setActiveMarketAdjustmentsTab] =
    useState<MarketAdjustmentsTab>('dateOfSale');

  const dispatch = useAppDispatch();

  const form = useMarketAdjustmentsModalForm();

  const compSequence = useAppSelector(selectCompSequence);
  const currentMarketAdjustments = useAppSelector(selectMarketAdjustments);
  const currentManualAdjustments = useAppSelector(selectManualAdjustmentsEntities);
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const transactionEntities = useAppSelector(selectTransactionEntities);
  const subjectValue = useAppSelector(selectSubjectValue);

  const zillowMethodInput = useMemo(() => {
    const zillowMethodState = currentMarketAdjustments.cachedForms.dateOfSale.methodState.zillow;

    if (isUndefined(effectiveDate) || isUndefined(zillowMethodState)) return undefined;

    return makeZillowMethodInput({
      effectiveDate: AloftDate.schema.parse(effectiveDate),
      compSequence,
      transactionEntities,
      zillowMethodState,
    });
  }, [
    currentMarketAdjustments.cachedForms.dateOfSale.methodState.zillow,
    compSequence,
    effectiveDate,
    transactionEntities,
  ]);

  const customMonthlyPrice = form.watch(
    'marketAdjustments.cachedForms.dateOfSale.methodState.customMonthlyPrice',
  );

  const customMonthlyPriceInput =
    effectiveDate &&
    makeCustomMonthlyPriceInput({
      effectiveDate: AloftDate.schema.parse(effectiveDate),
      trendsAsPercentages: customMonthlyPrice.trendsAsPercentages,
      trends: customMonthlyPrice.trends.map((trend) => ({
        month: trend.month as IsoMonth,
        value: AloftPrice.fromDollarLike(inputStringToNumber(trend.value)).toJSON(),
      })),
      compSequence,
      transactionEntities,
      subjectMarketPrice: subjectValue?.toJSON(),
    });

  const onSubmit = (data: MarketAdjustmentsModalForm) => {
    const updatedManualAdjustments = Object.entries(data.manualAdjustments)
      .filter(([id, formAdj]) => {
        const currAdj = expected(currentManualAdjustments[id]);

        const currConcessions = AloftPrice.schema.parse(currAdj.concessions);
        const nextConcessions = AloftPrice.fromDollarLike(formAdj.concessions);

        const currTermsOfSale = AloftPrice.schema.parse(currAdj.termsOfSale);
        const nextTermsOfSale = AloftPrice.fromDollarLike(formAdj.termsOfSale);

        const areConcessionsEqual = currConcessions.equals(nextConcessions);
        const areTermsOfSaleEqual = currTermsOfSale.equals(nextTermsOfSale);

        // Only dispatch changes for modified comps
        return !areConcessionsEqual || !areTermsOfSaleEqual;
      })
      .map(([id, adj]) => ({
        id,
        changes: {
          ...currentManualAdjustments[id],
          ...formManualAdjustmentsToState({
            concessions: adj.concessions,
            termsOfSale: adj.termsOfSale,
          }),
        },
      }));

    dispatch(setManualAdjustments(updatedManualAdjustments));
    dispatch(setMarketAdjustments(formMarketAdjustmentsToState(data.marketAdjustments)));
    if (data.commentary !== undefined) {
      dispatch(setMarketAdjustmentsCommentary({ commentary: data.commentary }));
    }
  };

  const submitForm = () =>
    form.handleSubmit(
      (formData) => {
        onSubmit(formData);
      },
      (e) => {
        console.error(e);
      },
    )();

  const submitFormRef = useRef(submitForm);
  submitFormRef.current = submitForm;

  const handleFormChange = useCallback(
    debounce((data: FormState<MarketAdjustmentsModalForm>) => {
      if (data.dirtyFields.marketAdjustments) {
        submitFormRef.current().catch((e) => console.error('Error handling form change:', e));
      }
    }, 50),
    [],
  );

  form.subscribe({
    name: 'marketAdjustments',
    callback: (data) => {
      handleFormChange(data as FormState<MarketAdjustmentsModalForm>);
    },
  });

  return (
    <Flex
      {...useTkv2ReferenceBarEvents()}
      className="market-adjustments-module"
      width="100%"
      gap={2}
      height="100%"
      overflow="scroll"
      paddingBottom="8px"
      flexDirection="column"
    >
      <FormProvider {...form}>
        <Form
          onBlur={() => {
            submitForm().catch((e) => console.error('Error on form blur:', e));
          }}
        >
          <Flex
            gap="8px"
            overflowX="auto"
            flexDirection="row"
            flexShrink={0}
            className="analysis-module"
            marginTop="8px"
            marginBottom="8px"
            paddingX="8px"
          >
            {compSequence.map((compID, index) => (
              <CompCard
                key={compID}
                compID={compID}
                index={index}
                zillowMethodInput={zillowMethodInput}
                customMonthlyPriceMethodInput={customMonthlyPriceInput}
              />
            ))}
          </Flex>
          <Block marginBottom="10px">
            <Tabs
              tabs={marketAdjustmentsTabs}
              activeTab={activeMarketAdjustmentsTab}
              setActiveTab={setActiveMarketAdjustmentsTab}
            />
          </Block>
          {activeMarketAdjustmentsTab === 'dateOfSale' && (
            <DateOfSaleTab
              zillowMethodInput={zillowMethodInput}
              customMonthlyPriceMethodInput={customMonthlyPriceInput}
            />
          )}
          {activeMarketAdjustmentsTab === 'listToSale' && <ListToSaleTab />}
        </Form>
      </FormProvider>
    </Flex>
  );
};
